import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  ViewChild,
  ViewEncapsulation,
  ElementRef,
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import {
  GridOptions,
  ColDef,
  GridReadyEvent,
  EditStrategyType,
  RowDataUpdatedEvent,
  RowClassParams,
  GridSizeChangedEvent,
  ColTypeDef,
} from 'ag-grid-enterprise';
import { AG_GRID_THEME } from './table-theme';

@Component({
  selector: 'app-table-grid',
  imports: [AgGridAngular],
  templateUrl: './table-grid.component.html',
  styleUrls: ['./table-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TableGridComponent {
  @ViewChild('tableGrid') tableGrid!: AgGridAngular;
  @ViewChild('gridDynamicHeight') gridDynamicHeight!: ElementRef;
  public gridOptions = input<GridOptions>();
  public gridColumns = input<ColDef[]>();
  public gridRowData = input<unknown[]>([]);
  public isDialog = input<boolean>();
  public addingNewRow = input<unknown[]>([]);
  public kpiTable = input<boolean>();
  public editType: EditStrategyType = 'singleCell';
  public columnTypes: Record<string, ColTypeDef> = {
    number: { filter: 'agNumberColumnFilter' },
    text: { filter: 'agTextColumnFilter' },
    date: { filter: 'agDateColumnFilter' },
  };

  constructor() {
    effect(() => {
      const updatedRowData = this.gridRowData();
      const updatedColumnData = this.gridColumns();
      if (this.tableGrid && this.tableGrid.api.getGridId() !== 'serverGrid') {
        this.tableGrid.api.setGridOption('rowData', updatedRowData);
      }
      if (this.tableGrid && this.tableGrid.api.getGridId() === 'serverGrid') {
        this.tableGrid.api.refreshServerSide({ purge: true });
      }
      if (this.tableGrid && this.tableGrid.api.getGridId() !== 'serverGrid') {
        this.tableGrid.api.setGridOption('columnDefs', updatedColumnData);
      }
    });
  }

  /**
   * Called when the grid is ready and the API is available
   *
   * @param {GridReadyEvent} params
   * @memberof TableGridComponent
   */
  public gridReady(params: GridReadyEvent): void {
    if (params.api) {
      this.gridDynamicHeight.nativeElement.style.height = 'calc(100vh - 250px)';
      if (params.api.getGridId() !== 'serverGrid') {
        params.api.setGridOption('rowData', this.gridRowData());
        this.gridDynamicHeight.nativeElement.style.height =
          'calc(100vh - 160px)';
        if (params.api.getGridId() === 'singleGrid') {
          this.gridDynamicHeight.nativeElement.style.height =
            'calc(100vh - 220px)';
        }
      }
      if (this.isDialog()) {
        this.gridDynamicHeight.nativeElement.style.height =
          'calc(100vh - 280px)';
      }
      if (this.kpiTable()) {
        this.gridDynamicHeight.nativeElement.style.height =
          'calc(100vh - 223px)';
      }
      params.api.setGridOption('theme', AG_GRID_THEME);
      params.api.setGridOption('columnDefs', this.gridColumns() ?? []);
      params.api.sizeColumnsToFit();
      params.api.setGridOption(
        'suppressPaginationPanel',
        this.gridRowData() ? false : true,
      );
      params.api.refreshHeader();
    }
  }
  /**
   * Returns a function to get the row ID for the grid.
   *
   * @param {*} event
   * @memberof TableGridComponent
   */
  public rowDataUpdated(event: RowDataUpdatedEvent) {
    const firstRow = event.api.getDisplayedRowAtIndex(0);
    if (firstRow && firstRow.data.isNewRow) {
      event.api.setGridOption('singleClickEdit', true);
      event.api.startEditingCell({
        rowIndex: 0,
        colKey: firstRow.data.activeField,
      });
      event.api.setGridOption('rowClassRules', {
        'new-row': (params: RowClassParams) => params.data.isNewRow as boolean,
      });
    }
  }

  /**
   * Called when the grid size changes
   *
   * @param {*} event
   * @memberof TableGridComponent
   */
  public onGridSizeChanged(event: GridSizeChangedEvent) {
    if (this.tableGrid) {
      event.api.sizeColumnsToFit();
    }
  }
}
